import {useCallback, useContext, useEffect, useState} from "react";
import axios from "axios";
import {TokenContext} from "../context/TokenContext";
import {FlashContext} from "../context/FlashContext";
import {CmrDocument} from "../model/CmrDocument";
import {CmrDocumentNormalizer} from "../services/normalizer/CmrDocumentNormalizer";
import {Commande} from "../model/Commande";

export interface UseCmrDocumentResult {
    document: CmrDocument | null;
    isUploading: boolean;
    upload: (file: File) => Promise<CmrDocument | null>;
    download: () => Promise<void>;
}

export function useCmrDocument(order: Commande): UseCmrDocumentResult {
    const {token} = useContext(TokenContext);
    const {addSuccess, addError} = useContext(FlashContext);

    const [document, setDocument] = useState<CmrDocument | null>(order.cmrDocument);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        setDocument(order.cmrDocument);
    }, [order]);

    const upload = useCallback(async (file: File): Promise<CmrDocument | null> => {
        if (!order.id) {
            return null;
        }

        const url = process.env.REACT_APP_API_URL;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploadedBy', localStorage.getItem('fullname') ?? 'Unknown');
        formData.append('commande', `/api/orders/${order.id}`);

        setIsUploading(true);
        try {
            const res = await axios.post(
                `${url}/api/cmr_documents`,
                formData,
                {
                    headers: {
                        Accept: 'application/json',
                        Authorization: 'Token ' + token,
                        'Content-Type': 'multipart/form-data',
                    },
                },
            );
            if (!res || !res.data) {
                addError('Erreur lors du téléversement du CMR');
                return null;
            }
            const created = CmrDocumentNormalizer.normalize(res.data);
            setDocument(created);
            addSuccess('CMR ajouté');
            return created;
        } catch (err) {
            addError('Erreur lors du téléversement du CMR');
            return null;
        } finally {
            setIsUploading(false);
        }
    }, [order.id, token, addSuccess, addError]);

    const download = useCallback(async (): Promise<void> => {
        if (!order.id || !document) {
            return;
        }

        const url = process.env.REACT_APP_API_URL;
        try {
            const res = await axios.get<Blob>(
                `${url}/api/orders/${order.id}/cmr-file`,
                {
                    headers: {
                        Accept: 'application/json',
                        Authorization: 'Token ' + token,
                    },
                    responseType: 'blob',
                },
            );
            if (!res || !res.data) {
                addError('Téléchargement indisponible');
                return;
            }
            const blobUrl = window.URL.createObjectURL(res.data);
            const a = window.document.createElement('a');
            a.download = document.filename;
            a.href = blobUrl;
            a.click();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            addError('Erreur lors du téléchargement du CMR');
        }
    }, [order.id, token, document, addError]);

    return {document, isUploading, upload, download};
}
